import React, { useState, useEffect, useRef, useMemo } from "react";
import { FaBuilding, FaPlus } from "react-icons/fa";
import useAuth from "../../../../Hooks/useAuth";
import usePermission from "../../../../Hooks/usePermission";
import { ALL_FIRM_TAB_PERMISSIONS, ALL_DEALER_FIRM_TAB_PERMISSIONS } from "../../../../utils/firmUtils";
import { useBelowFirmsData } from "../../../../Hooks/useBelowFirmsData";
import { useDebounce } from "../../../../Hooks/useDebounce";
import {
  useSearchUnlinkedFirmsMutation,
  useLinkSubFirmMutation,
  useUnlinkSubFirmMutation,
  useSendSubFirmQuotaMutation,
  useLazyGetSendableSubFirmQuotaQuery,
} from "../../../../store";
import { MascotLoadingOverlay, EmptyState, SearchInput } from "../../../shared";
import ConfirmationModal from "../ConfirmationModal";
import SubFirmQuotaDrawer from "../SubFirmQuotaDrawer";
import SubFirmsTable from "../SubFirmsTable";
import SubFirmsTableSkeleton from "../SubFirmsTableSkeleton";
import FirmBusyBadge from "../../FirmBusyBadge";
import useFirmBusy from "../../../../Hooks/dealerRoles/useFirmBusy";
import QuotaSplitCards, {
  sumFirmsQuotaByProduct,
  firmProductQuotaSummary,
} from "../QuotaSplitCards";
import { Alert } from "@mui/material";

/**
 * Firma > Alt Firmalar sekmesi. Bayi firmada (#376) tek "Müşteriler" sekmesi
 * olur: `customersSource` verilince bayinin müşterileri alt bayilerle AYNI
 * listeye girer; satır tipine göre (alt bayi / müşteri) rozet, bağı kopar ucu
 * ve kota kuralı seçilir. `includeSubDealers` false ise (izin yok) alt bayi
 * satırları çekilmez.
 *
 * customersSource: { customers, loading, refetch, canSendQuota, canUnlink,
 *   unlink(customer) → Promise, unlinkLoading, limitToPool, requireLiveSource }
 */
const SubFirmsTab = ({
  firm,
  onSelectedFirm,
  title,
  externalData,
  externalHooks,
  customersSource = null,
  includeSubDealers = true,
}) => {
  const { auth } = useAuth();
  const { hasPermission, hasAnyPermission } = usePermission();
  // Alt firma satırlarına tıklayıp o firmanın kendi detayına gitme, üst
  // düzey firma listelerindeki (FirmCard.js) canNavigate ile aynı kurala
  // tabidir: hedef firmanın herhangi bir tab iznine sahip olmak gerekir.
  const canNavigateToSubFirm = hasAnyPermission(
    ...(auth?.role === "dealer" ? ALL_DEALER_FIRM_TAB_PERMISSIONS : ALL_FIRM_TAB_PERMISSIONS)
  );
  const [search, setSearch] = useState("");
  const [alert, setAlert] = useState(null);
  const displayTitle = title || "Alt Firmalar";
  const isDealerFirm = firm?.firmType === "dealer";
  // Bayi kuralları (#303): Kota Ver yalnız havuzdan, Kota Al canlı okumalı.
  const isDealerUser = auth?.role === "dealer";

  // Dropdown states
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [dropdownSearch, setDropdownSearch] = useState("");
  const [dropdownResults, setDropdownResults] = useState([]);
  const dropdownRef = useRef(null);
  const debouncedDropdownSearch = useDebounce(dropdownSearch, 400);
  const debouncedSearch = useDebounce(search, 250);

  // Confirmation modal states
  const [confirmLink, setConfirmLink] = useState(null);
  const [confirmUnlink, setConfirmUnlink] = useState(null);

  // Kota transferi drawer'i — satirdaki iki butondan biriyle acilir.
  // { firm, direction: "toSub" | "toParent" }
  const [quotaDrawer, setQuotaDrawer] = useState(null);

  const canLink = hasPermission("firms.belowFirmsLink");
  const canUnlink = hasPermission("firms.belowFirmsUnlink");
  const canSendQuota = hasPermission(
    auth?.role === "dealer" ? "dealers.sendSubFirmQuota" : "firms.sendSubFirmQuota",
  );

  const internalData = useBelowFirmsData(
    externalData || !includeSubDealers
      ? { id: null, token: null }
      : { id: firm?._id, token: auth.accessToken },
  );
  const subDealerData = includeSubDealers ? externalData || internalData : {};
  const belowFirms = subDealerData.belowFirms;
  const belowFirmsIsLoading = !!subDealerData.belowFirmsIsLoading || !!customersSource?.loading;
  const belowFirmsIsFetching = !!subDealerData.belowFirmsIsFetching;

  // Bayide tek Müşteriler sekmesi (#376): müşteri satırları işaretlenir
  const customerRows = useMemo(
    () => (customersSource?.customers || []).map((c) => ({ ...c, __row: "customer" })),
    [customersSource?.customers],
  );
  const isCustomerRow = (row) => row?.__row === "customer";
  const canSendCustomerQuota = !!customersSource?.canSendQuota;
  const canUnlinkCustomer = !!customersSource?.canUnlink;

  // Use external hooks for dealer or internal for storage
  const [searchUnlinkedInt, { isLoading: searchLoadingInt }] = useSearchUnlinkedFirmsMutation();
  const [linkSubFirmInt, { isLoading: linkLoadingInt }] = useLinkSubFirmMutation();
  const [unlinkSubFirmInt, { isLoading: unlinkLoadingInt }] = useUnlinkSubFirmMutation();
  const [sendQuotaInt, { isLoading: sendQuotaLoadingInt }] = useSendSubFirmQuotaMutation();
  const [fetchSendableInt, { data: sendableInt, isFetching: sendableFetchingInt }] =
    useLazyGetSendableSubFirmQuotaQuery();

  const searchUnlinked = externalHooks?.searchUnlinked || searchUnlinkedInt;
  const searchLoading = externalHooks?.searchLoading ?? searchLoadingInt;
  const linkSubFirm = externalHooks?.linkSubFirm || linkSubFirmInt;
  const linkLoading = externalHooks?.linkLoading ?? linkLoadingInt;
  const unlinkSubFirm = externalHooks?.unlinkSubFirm || unlinkSubFirmInt;
  const unlinkLoading = externalHooks?.unlinkLoading ?? unlinkLoadingInt;
  const sendQuota = externalHooks?.sendQuota || sendQuotaInt;
  const sendQuotaLoading = externalHooks?.sendQuotaLoading ?? sendQuotaLoadingInt;
  const fetchSendable = externalHooks?.fetchSendable || fetchSendableInt;
  const sendableResponse = externalHooks?.sendable ?? sendableInt;
  const sendableFetching = externalHooks?.sendableFetching ?? sendableFetchingInt;

  // Firmanin KENDI urun kota ozeti — hem sekme basliginda gosterilir hem de
  // "Kota Ver" drawer'ina gonderilebilir bakiye olarak beslenir.
  const sendableQuota = useMemo(
    () => sendableResponse?.data || [],
    [sendableResponse],
  );

  useEffect(() => {
    if (firm?._id && auth?.accessToken) {
      fetchSendable({ id: firm._id, token: auth.accessToken });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [firm?._id, auth?.accessToken]);

  // Bağ koparma/kurma sonrası liste ANINDA güncellenir: sunucudan gelen
  // yeni listeyi beklemek yerine ilgili satır hemen düşer / eklenir.
  // (RTK invalidation refetch'i arkadan gelir ve örtüyü kendiliğinden temizler.)
  const [removedIds, setRemovedIds] = useState([]);
  const [addedFirms, setAddedFirms] = useState([]);

  // Sunucu listesi güncellendiğinde örtüyü buda: çıkarılan firma artık
  // listede yoksa id'yi tut, eklenen firma artık listede varsa çıkar.
  useEffect(() => {
    const serverIds = new Set([...(belowFirms || []), ...customerRows].map((f) => f._id));
    // Değişiklik yoksa aynı diziyi döndür — gereksiz render döngüsü olmasın
    setRemovedIds((prev) => {
      const next = prev.filter((id) => serverIds.has(id));
      return next.length === prev.length ? prev : next;
    });
    setAddedFirms((prev) => {
      const next = prev.filter((f) => !serverIds.has(f._id));
      return next.length === prev.length ? prev : next;
    });
  }, [belowFirms, customerRows]);

  const subDealerIds = new Set((belowFirms || []).map((f) => String(f._id)));
  const visibleFirms = [
    ...(belowFirms || []).filter((f) => !removedIds.includes(f._id)),
    ...addedFirms,
    // Müşteriler alt bayilerin ardından; aynı firma iki kez listelenmez
    ...customerRows.filter((c) => !subDealerIds.has(String(c._id)) && !removedIds.includes(c._id)),
  ];

  // Firma meşguliyeti (#303) — üst firma meşgulse tüm satırlar da kilitli
  // (transfer iki firmanın kilidini birden alır).
  const { busyIds: rawBusyIds, isBusy } = useFirmBusy([
    firm?._id,
    ...(visibleFirms || []).map((f) => f._id),
  ]);
  const parentBusy = isBusy(firm?._id);
  const busyIds = parentBusy
    ? new Set((visibleFirms || []).map((f) => String(f._id)))
    : rawBusyIds;

  // Arama gecikmeli (#407): her tuşta tüm satırlar yeniden süzülüp tablo
  // baştan çizilmesin. bayserNo sayı gelebilir — String ile karşılaştırılır.
  const filteredFirms = useMemo(() => {
    const s = debouncedSearch.toLowerCase();
    if (!s) return visibleFirms || [];
    return (visibleFirms || []).filter((subFirm) =>
      [subFirm.firmName, subFirm.bayserNo, subFirm.email, subFirm.tel].some((v) =>
        v != null && String(v).toLowerCase().includes(s)
      )
    );
  }, [visibleFirms, debouncedSearch]);

  const isLoading = belowFirmsIsLoading;
  // Ürün kota kartlarının sağ yarısı ALT FİRMA verisinden hesaplanıyor; alt
  // firmalar yenilenirken kartlar da skeleton'a döner, yoksa eski toplamı
  // gösterip sonra zıplıyorlar.
  const cardsLoading = sendableFetching || belowFirmsIsLoading || belowFirmsIsFetching;

  // Alert timeout
  useEffect(() => {
    if (alert) {
      const timer = setTimeout(() => setAlert(null), 5000);
      return () => clearTimeout(timer);
    }
  }, [alert]);

  // Debounce search for dropdown
  useEffect(() => {
    if (!debouncedDropdownSearch || debouncedDropdownSearch.length < 2) {
      setDropdownResults([]);
      return;
    }
    const doSearch = async () => {
      try {
        const result = await searchUnlinked({
          token: auth.accessToken,
          search: debouncedDropdownSearch,
          parentFirmId: firm._id,
        }).unwrap();
        setDropdownResults(result.data || []);
      } catch {
        setDropdownResults([]);
      }
    };
    doSearch();
  }, [debouncedDropdownSearch]);

  // Close dropdown on outside click
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
        setDropdownOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const handleLinkFirm = async (subFirmId) => {
    try {
      await linkSubFirm({
        token: auth.accessToken,
        subFirmId,
        parentFirmId: firm._id,
      }).unwrap();
      setAlert({ severity: "success", message: "Firma alt firma olarak başarıyla bağlandı" });
      // Yeni firma listede ANINDA görünür; dropdown sonucundan alınır.
      const linked = dropdownResults.find((f) => f._id === subFirmId);
      if (linked) setAddedFirms((prev) => [...prev, linked]);
      setDropdownResults((prev) => prev.filter((f) => f._id !== subFirmId));
      setConfirmLink(null);
      // RTK Query invalidation refetch'i arkadan gelir ve örtüyü temizler.
    } catch (err) {
      setAlert({ severity: "error", message: err?.data?.message || "Bağlama sırasında hata oluştu" });
      setConfirmLink(null);
    }
  };

  const handleUnlinkFirm = async (subFirmId) => {
    // Müşteri satırı: bayi bağını koparır (Müşteriler sekmesindeki uç, #202)
    if (confirmUnlink && isCustomerRow(confirmUnlink)) {
      try {
        await customersSource.unlink(confirmUnlink);
        setAlert({ severity: "success", message: `${confirmUnlink.firmName} firmasının bayi bağı koparıldı` });
        setRemovedIds((prev) => [...prev, subFirmId]);
        setConfirmUnlink(null);
        customersSource.refetch?.();
        fetchSendable({ id: firm._id, token: auth.accessToken });
      } catch (err) {
        setAlert({ severity: "error", message: err?.data?.message || "Bayi bağı koparılamadı" });
        setConfirmUnlink(null);
      }
      return;
    }
    try {
      await unlinkSubFirm({
        token: auth.accessToken,
        subFirmId,
      }).unwrap();
      setAlert({ severity: "success", message: "Alt firma başarıyla çıkarıldı" });
      // Satır ANINDA düşer — sunucudan yeni listenin gelmesi beklenmez.
      setRemovedIds((prev) => [...prev, subFirmId]);
      setConfirmUnlink(null);
      // RTK Query invalidation refetch'i arkadan gelir ve örtüyü temizler.
    } catch (err) {
      setAlert({ severity: "error", message: err?.data?.message || "Çıkarma sırasında hata oluştu" });
      setConfirmUnlink(null);
    }
  };

  // GERİ AL yönünde kaynak ALT firmadır; onun kota özeti ayrı bir istek
  // gerektirmez — `belowFirms` yanıtı zaten ürün başına havuz (`poolQuota`) ve
  // cihaz toplamını (`quota`) taşıyor (bkz. firm-base.controller).
  const subFirmQuotaSummary = useMemo(
    () => firmProductQuotaSummary(quotaDrawer?.firm),
    [quotaDrawer],
  );

  const handleTransferQuota = async ({
    subFirmId,
    productName,
    quota,
    note,
    direction,
  }) => {
    try {
      const res = await sendQuota({
        token: auth.accessToken,
        id: firm._id,
        subFirmId,
        productName,
        quota,
        note: note || "",
        direction,
      }).unwrap();
      setAlert({
        severity: "success",
        message: res?.message || "Kota başarıyla aktarıldı",
      });
      if (isCustomerRow(quotaDrawer?.firm)) customersSource?.refetch?.();
      setQuotaDrawer(null);
      // Bu firmanin bakiyesi degisti — baslikta bayat deger kalmasin.
      fetchSendable({ id: firm._id, token: auth.accessToken });
    } catch (err) {
      setAlert({
        severity: "error",
        message: err?.data?.message || "Kota aktarılırken hata oluştu",
      });
    }
  };

  // BAŞLIKTAKI KOTA KARTLARI (#300) — sol: bu firmanın kendisinde, sunucudan
  // gelen `total` = havuz + kendi cihazları. Sağ: listedeki firmaların ürün
  // bazında toplamı (havuz + TÜM cihazlar, dağıtımı kapalı cihaz dahil);
  // kendi productInfo'larından toplanır, ayrı istek gerekmez.
  const subFirmTotalsByProduct = useMemo(
    () => sumFirmsQuotaByProduct(visibleFirms || []),
    [visibleFirms],
  );

  const productsForCards = useMemo(() => {
    const rows = sendableQuota.map((p) => ({
      productName: p.productName,
      left: p.total ?? 0,
      right: subFirmTotalsByProduct.get(p.productName) ?? 0,
    }));
    // Birleşik sekmede yalnız müşteride olan ürünler de kartta görünür
    if (customersSource) {
      const known = new Set(rows.map((r) => r.productName));
      subFirmTotalsByProduct.forEach((total, productName) => {
        if (!known.has(productName)) rows.push({ productName, left: 0, right: total });
      });
    }
    return rows;
  }, [sendableQuota, subFirmTotalsByProduct, customersSource]);

  const merged = !!customersSource;
  const linkLabel = isDealerFirm ? "Alt Bayi Ekle" : "Alt Firma Ekle";

  return (
    <div className="flex flex-col h-full w-full">
      {/* Alert */}
      {alert && (
        <div className="fixed z-50 left-1/2 top-4 -translate-x-1/2 w-[90%] max-w-md">
          <Alert
            severity={alert.severity}
            onClose={() => setAlert(null)}
            variant="filled"
            sx={{ boxShadow: "0 4px 12px rgba(0,0,0,0.15)", borderRadius: "12px", fontSize: "13px" }}
          >
            {alert.message}
          </Alert>
        </div>
      )}

      {/* Sticky Header — Cihazlar sekmesiyle aynı iskelet: Satır 1 başlık +
          arama + aksiyon, Satır 2 ürün kota kartları. */}
      <div className="sticky top-0 z-10 bg-white/95 backdrop-blur-sm border-b border-gray-100 shrink-0 px-2 sm:px-4 py-3 sm:py-4 space-y-2.5">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 bg-indigo-50 rounded-xl flex items-center justify-center shrink-0">
            <FaBuilding className="w-4 h-4 text-indigo-500" />
          </div>
          <div className="shrink-0">
            <h2 className="text-base sm:text-lg font-Bold text-gray-800 flex items-center gap-2">
              {displayTitle}
              {parentBusy && <FirmBusyBadge />}
            </h2>
            <p className="text-xs text-gray-400">
              {isLoading ? "Yükleniyor..." : `${filteredFirms.length} firma kayıtlı`}
            </p>
          </div>

          <SearchInput
            placeholder="Firma adı, bayser no, email ara..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            wrapperClassName="flex-1 min-w-0 ml-auto max-w-md"
          />

          {/* Alt Firma Ekle Dropdown */}
          {canLink && (
            <div className="relative shrink-0" ref={dropdownRef}>
              <button
                type="button"
                onClick={() => {
                  setDropdownOpen(!dropdownOpen);
                  setDropdownSearch("");
                  setDropdownResults([]);
                }}
                className="flex items-center gap-1.5 px-3 py-2 bg-indigo-500 text-white rounded-xl text-xs font-semibold hover:bg-indigo-600 transition-colors"
              >
                <FaPlus className="w-3 h-3" />
                <span className="hidden sm:inline">{linkLabel}</span>
              </button>

              {dropdownOpen && (
                <div className="absolute right-0 top-full mt-1 w-80 bg-white border border-gray-200 rounded-xl shadow-xl z-50">
                  <div className="p-3 border-b border-gray-100">
                    <input
                      type="text"
                      autoFocus
                      placeholder="Firma adı, bayser no veya email ile arayın..."
                      value={dropdownSearch}
                      onChange={(e) => setDropdownSearch(e.target.value)}
                      className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-300 focus:border-indigo-400 outline-none"
                    />
                  </div>

                  <div className="max-h-60 overflow-y-auto">
                    {searchLoading ? (
                      <div className="flex items-center justify-center py-6">
                        <div className="w-5 h-5 border-2 border-indigo-400 border-t-transparent rounded-full animate-spin" />
                      </div>
                    ) : dropdownSearch.length < 2 ? (
                      <p className="text-xs text-gray-400 text-center py-6">
                        En az 2 karakter girin
                      </p>
                    ) : dropdownResults.length === 0 ? (
                      <p className="text-xs text-gray-400 text-center py-6">
                        Sonuç bulunamadı
                      </p>
                    ) : (
                      <div className="py-1">
                        {dropdownResults.map((f) => (
                          <div
                            key={f._id}
                            className="flex items-center gap-2.5 px-3 py-2.5 hover:bg-indigo-50 cursor-pointer transition-colors"
                            onClick={() => setConfirmLink(f)}
                          >
                            <div className="w-7 h-7 bg-indigo-50 rounded-full flex items-center justify-center shrink-0">
                              <FaBuilding className="w-2.5 h-2.5 text-indigo-600" />
                            </div>
                            <div className="flex-1 min-w-0">
                              <p className="text-sm font-semibold text-gray-800 truncate">
                                {f.firmName}
                              </p>
                              <p className="text-[11px] text-gray-500 truncate">
                                {f.bayserNo}{f.email ? ` · ${f.email}` : ""}
                              </p>
                            </div>
                            <FaPlus className="w-3 h-3 text-indigo-500 shrink-0" />
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
              )}
            </div>
          )}
        </div>

        {/* Satır 2: Ürün Kota Kartları — sol: bu firmanın kendisinde (havuz +
            kendi cihazları), dik çizginin sağında: listedeki firmalarda.
            "Dağıtılan" dili yok (#300). */}
        <QuotaSplitCards
          items={productsForCards}
          loading={cardsLoading}
          leftLabel={isDealerFirm ? "Bayide" : "Firmada"}
          rightLabel={merged ? "Müşterilerde" : isDealerFirm ? "Alt Bayilerde" : "Alt Firmalarda"}
        />
      </div>

      {/* İçerik — KENAR BOŞLUKSUZ (full-bleed). Cihazlar sekmesiyle aynı:
          tablo kendi kabuğunda kayar, yatay taşma tablonun içinde kalır. */}
      {isLoading ? (
        /* Kural: MascotLoadingOverlay her zaman skeleton'ın ÜSTÜNDE
           (bkz. docs/rules/ui-components.md). */
        <div className="relative flex-1 min-h-0">
          <MascotLoadingOverlay message="Firmalar yükleniyor..." />
          <SubFirmsTableSkeleton rows={6} />
        </div>
      ) : filteredFirms.length === 0 ? (
        <div className="flex-1 overflow-y-auto min-h-0 px-4 py-4">
          <EmptyState
            message={
              merged
                ? "Müşteri Bulunamadı"
                : displayTitle === "Alt Bayiler"
                  ? "Alt Bayi Bulunamadı"
                  : "Alt Firma Bulunamadı"
            }
            suggestion={
              search
                ? "Arama kriterlerinizi değiştirmeyi deneyin."
                : merged
                  ? "Bu bayinin altında henüz alt bayi ya da müşteri yok."
                  : displayTitle === "Alt Bayiler"
                    ? "Bu bayinin altında henüz alt bayi eklenmemiş."
                    : "Bu firmaya henüz alt firma eklenmemiş."
            }
          />
        </div>
      ) : (
        <div className="flex-1 min-h-0">
          <SubFirmsTable
            firms={filteredFirms}
            canNavigate={canNavigateToSubFirm}
            canSendQuota={canSendQuota || canSendCustomerQuota}
            canSendQuotaFor={(row) => (isCustomerRow(row) ? canSendCustomerQuota : canSendQuota)}
            canUnlink={canUnlink || canUnlinkCustomer}
            canUnlinkFor={(row) => (isCustomerRow(row) ? canUnlinkCustomer : canUnlink)}
            showTypeBadge={merged}
            onSendQuota={(sub) => setQuotaDrawer({ firm: sub, direction: "toSub" })}
            onTakeBackQuota={(sub) =>
              setQuotaDrawer({ firm: sub, direction: "toParent" })
            }
            onUnlink={setConfirmUnlink}
            busyIds={busyIds}
          />
        </div>
      )}

      {/* Ekleme Onay Modalı */}
      <ConfirmationModal
        open={!!confirmLink}
        onClose={() => setConfirmLink(null)}
        onConfirm={() => confirmLink && handleLinkFirm(confirmLink._id)}
        title="Alt Firma Ekle"
        message={confirmLink ? `${confirmLink.firmName} firmasını alt firma olarak bağlamak istediğinize emin misiniz?` : ""}
        confirmText="Ekle"
        cancelText="İptal"
        isLoading={linkLoading}
      />

      {/* Kota transferi drawer'ı (Kota Ver / Kota Geri Al) */}
      <SubFirmQuotaDrawer
        open={!!quotaDrawer}
        onClose={() => setQuotaDrawer(null)}
        onSubmit={handleTransferQuota}
        subFirm={quotaDrawer?.firm}
        direction={quotaDrawer?.direction || "toSub"}
        parentFirmName={firm?.firmName}
        sendableQuota={sendableQuota}
        subFirmQuota={subFirmQuotaSummary}
        summaryLoading={sendableFetching}
        isLoading={sendQuotaLoading}
        limitToPool={isCustomerRow(quotaDrawer?.firm) ? !!customersSource?.limitToPool : isDealerUser}
        requireLiveSource={isCustomerRow(quotaDrawer?.firm) ? !!customersSource?.requireLiveSource : isDealerUser}
      />

      {/* Çıkarma Onay Modalı */}
      <ConfirmationModal
        open={!!confirmUnlink}
        onClose={() => setConfirmUnlink(null)}
        onConfirm={() => confirmUnlink && handleUnlinkFirm(confirmUnlink._id)}
        title={isCustomerRow(confirmUnlink) ? "Bayi Bağını Kopar" : "Alt Firmayı Çıkar"}
        message={
          !confirmUnlink
            ? ""
            : isCustomerRow(confirmUnlink)
              ? `${confirmUnlink.firmName} firmasının ${firm?.firmName} bayisiyle bağını koparmak istediğinize emin misiniz? Firma bu bayinin müşteri listesinden çıkar; kotası ve makineleri firmada kalır.`
              : `${confirmUnlink.firmName} firmasını alt firma listesinden çıkarmak istediğinize emin misiniz?`
        }
        confirmText={isCustomerRow(confirmUnlink) ? "Bağı Kopar" : "Çıkar"}
        cancelText="İptal"
        isLoading={unlinkLoading || !!customersSource?.unlinkLoading}
      />
    </div>
  );
};

export default SubFirmsTab;
